/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ElementType, ReactElement } from 'react';
import * as React from 'react';
import type { ReactMarkdownProps } from 'react-markdown';
import Markdown from 'react-markdown';

import { Typography2 } from '@coursera/cds-core';
import { SvgCheck } from '@coursera/coursera-ui/svg';
import type { ButtonName, PageSection, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

// Custom styles for AI summary markdown (spacing and non-typography styles only)
const aiSearchMarkdownStyles = css`
  /* Link styles */
  a {
    text-decoration: underline;
    word-wrap: break-word;
    color: var(--cds-color-blue-700);
  }

  /* List spacing */
  ul {
    list-style-type: disc;
    padding-left: var(--cds-spacing-200);
    margin-left: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);
  }

  ol {
    padding-left: 10px;
    margin-left: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);
  }

  li {
    margin-top: 0;
    margin-bottom: 0;
  }

  p {
    margin-top: 0;
    margin-bottom: var(--cds-spacing-100);
  }
`;

const paragraphSpacing = css`
  margin-top: var(--cds-spacing-100);
  margin-bottom: var(--cds-spacing-200);
  min-height: 20px;
`;

const listItemSpacing = css`
  margin-top: var(--cds-spacing-100);
  margin-bottom: var(--cds-spacing-100);
`;

type Props = Omit<ReactMarkdownProps, 'source'> & {
  source?: string | null;
  className?: string;
  linkTarget?: string;
  onLinkClick?: () => void;
  trackingName?: string;
  buttonTrackingNameV3?: ButtonName;
  trackingSection?: SectionName;
  trackingSubSection?: PageSection['subsectionName'];
  trackingIndex?: number;
  trackingProductId?: string;
  rel?: string;
  showSvgCheck?: boolean;
  renderers?: { [nodeType: string]: ElementType };
  isExpanded?: boolean;
};

const textToSlug = (text: React.ReactChild) =>
  text
    .toString()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9- ]/g, '')
    .replace(/ +/g, '-');

const Heading = ({ children, level }: { children?: React.ReactChild[]; level: number }) => {
  if (!children) {
    return null;
  }

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const slug = textToSlug(children[0]);

  // Map heading levels to Typography2 variants and components
  switch (level) {
    case 1:
      return (
        <Typography2 variant="titleLarge" component="h1" id={slug}>
          {children}
        </Typography2>
      );
    case 2:
      return (
        <Typography2 variant="titleMedium" component="h2" id={slug}>
          {children}
        </Typography2>
      );
    case 3:
      return (
        <Typography2 variant="subtitleLarge" component="h3" id={slug}>
          {children}
        </Typography2>
      );
    case 4:
      return (
        <Typography2 variant="subtitleMedium" component="h4" id={slug}>
          {children}
        </Typography2>
      );
    case 5:
      return (
        <Typography2 variant="subtitleSmall" component="h5" id={slug}>
          {children}
        </Typography2>
      );
    case 6:
    default:
      return (
        <Typography2 variant="subtitleSmall" component="h6" id={slug}>
          {children}
        </Typography2>
      );
  }
};

type ListItemProps = {
  children?: Array<string | ReactElement>;
};

const renderListItem = (props: ListItemProps, showSvgCheck: boolean | undefined) => {
  const { children } = props;

  if (!children || !children.length) {
    return null;
  }
  const listItemArrayFirstItem = children[0];
  let key;
  // Means the list item include at least one React Element, e.g. <strong></strong>, then we need to use the text content as the key
  if (typeof listItemArrayFirstItem === 'object' && !!listItemArrayFirstItem?.props?.children?.[0]) {
    key = listItemArrayFirstItem.props.children[0];
    // This case helps account for invalid formatting from Contentful. Sometimes it will not have a "child", but will have "value".
  } else if (typeof listItemArrayFirstItem === 'object' && !!listItemArrayFirstItem?.props?.value) {
    key = listItemArrayFirstItem.props.value;
  } else if (typeof listItemArrayFirstItem === 'string') {
    // string should handle all other cases
    key = listItemArrayFirstItem;
  }
  return (
    <Typography2 variant="bodyPrimary" component="li" role="listitem" key={key} css={listItemSpacing}>
      {showSvgCheck && (
        <SvgCheck
          className="checkIcon"
          suppressTitle={true}
          width="24px"
          height="24px"
          style={{
            left: '0',
            marginRight: '1rem',
            marginTop: '-1px',
            position: 'absolute',
            top: '0',
            fill: 'var(--cds-color-green-600)',
          }}
        />
      )}
      {children}
    </Typography2>
  );
};

type ListProps = {
  ordered: boolean;
  children?: React.ReactElement<ListItemProps>[];
};

const renderList = (props: ListProps, showSvgCheck: boolean | undefined) => {
  const { children, ordered } = props;
  if (!children || !children.length) {
    return null;
  }

  const Tag = ordered ? 'ol' : 'ul';

  // Ignore to enable VoiceOver: https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html
  return <Tag role="list">{children.map((child) => renderListItem(child.props, showSvgCheck))}</Tag>;
};

type ComponentProps<T> = T extends React.Component<infer P> ? P : never;

function AISearchMarkdown({
  source,
  className,
  trackingName,
  linkTarget = '_blank',
  onLinkClick,
  buttonTrackingNameV3,
  trackingSection,
  trackingSubSection,
  trackingIndex,
  trackingProductId,
  rel,
  showSvgCheck,
  renderers,
  isExpanded,
  ...markdownProps
}: Props) {
  const track = useTracker();
  if (!source) {
    return null;
  }

  const customRenderers = {
    link: (props: ComponentProps<TrackedA>) => {
      const { href } = props;

      const linkType = typeof href === 'string' && isExternalLink(href) ? 'external' : 'internal';

      const handleOnClick = () => {
        if (buttonTrackingNameV3) {
          track('click_button', {
            button: {
              name: buttonTrackingNameV3,
              linkURL: href,
              linkType,
            },
            ...(trackingSection && {
              pageSection: {
                sectionName: trackingSection,
                ...(trackingSubSection && {
                  subsectionName: trackingSubSection,
                }),
                ...(trackingIndex && {
                  subindex: trackingIndex,
                }),
              },
            }),
            ...(trackingProductId && {
              product: {
                id: trackingProductId,
              },
            }),
          });
        }
        if (onLinkClick) {
          onLinkClick();
        }
      };

      return (
        <TrackedA
          {...props}
          trackingName={trackingName || 'link_in_contentful'}
          data={{ href }}
          onClick={handleOnClick}
          rel={rel}
        />
      );
    },
    paragraph: (props: { children?: React.ReactNode }) => (
      <Typography2 variant="bodyPrimary" component="p" css={paragraphSpacing}>
        {props.children}
      </Typography2>
    ),
    heading: Heading,
    list: (props: ListProps) => renderList(props, showSvgCheck),
    ...renderers,
  };

  return (
    <Markdown
      {...markdownProps}
      css={aiSearchMarkdownStyles}
      source={source}
      linkTarget={linkTarget}
      renderers={customRenderers}
    />
  );
}

export default AISearchMarkdown;
